import React, { useEffect, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
  increment,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { ensureConversation } from "./chatHelpers";
import { useConversationMessages } from "./useConversationMessages";

function formatTime(ts) {
  if (!ts) return "";
  const d =
    typeof ts.toDate === "function"
      ? ts.toDate()
      : ts instanceof Date
      ? ts
      : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("sq-AL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const statusConfig = {
  pending: {
    label: "In Waiting",
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.08)",
    icon: "⏳",
  },
  confirmed: {
    label: "Confirmed",
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.08)",
    icon: "✓",
  },
  completed: {
    label: "Completed",
    color: "#6366f1",
    bg: "rgba(99, 102, 241, 0.08)",
    icon: "✓",
  },
  cancelled: {
    label: "Cancelled",
    color: "#ef4444",
    bg: "rgba(239, 68, 68, 0.08)",
    icon: "✕",
  },
};

function AppointmentChat({
  appointmentId,
  doctorId,
  patientId,
  onClose,
  doctorName,
  patientName,
  serviceName,
  appointmentStatus,
  mode = "doctor", // Add mode prop to know perspective
}) {
  const user = auth.currentUser;
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const isClosed =
    (appointmentStatus || "").toLowerCase() === "completed" ||
    (appointmentStatus || "").toLowerCase() === "cancelled";

  const isDoctor = user && user.uid === doctorId;
  const isPatient = user && user.uid === patientId;

  const otherName = isDoctor
    ? patientName || "Patient"
    : doctorName || "Doctor";
  
  const status = statusConfig[(appointmentStatus || "confirmed").toLowerCase()] || 
                 statusConfig.confirmed;

  useEffect(() => {
    async function initConv() {
      if (!user) return;
      const convId = await ensureConversation({
        appointmentId,
        doctorId,
        patientId,
        doctorName,
        patientName,
        appointmentStatus,
        serviceName,
      });
      setConversationId(convId);
    }
    initConv();
  }, [
    appointmentId,
    doctorId,
    patientId,
    user,
    doctorName,
    patientName,
    appointmentStatus,
    serviceName,
  ]);

  const { messages, loadingMessages } = useConversationMessages(conversationId);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  if (!user) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorMessage}>
          Please log in to access chat
        </div>
      </div>
    );
  }

  if (!isDoctor && !isPatient) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorMessage}>
          You don't have access to this conversation
        </div>
      </div>
    );
  }

  async function handleSend(e) {
    e.preventDefault();
    if (isClosed) return;
    if (!conversationId || !input.trim()) return;

    setSending(true);
    const text = input.trim();
    setInput("");

    const messagesRef = collection(
      db,
      "conversations",
      conversationId,
      "messages"
    );

    await addDoc(messagesRef, {
      senderId: user.uid,
      text,
      createdAt: serverTimestamp(),
      isRead: false,
    });

    const convRef = doc(db, "conversations", conversationId);
    const unreadUpdate = isDoctor
      ? { unreadPatient: increment(1) }
      : { unreadDoctor: increment(1) };

    await updateDoc(convRef, {
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: text.slice(0, 120),
      appointmentStatus: appointmentStatus || null,
      isClosed,
      ...unreadUpdate,
    });

    setSending(false);
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.avatar}>
            {otherName?.[0]?.toUpperCase() || "?"}
          </div>
          <div style={styles.headerInfo}>
            <div style={styles.headerName}>{otherName}</div>
            <div style={styles.headerSubtitle}>
              {serviceName || "Consultation"}
            </div>
          </div>
        </div>

        <div style={styles.headerRight}>
          <div
            style={{
              ...styles.statusBadge,
              background: status.bg,
              color: status.color,
            }}
          >
            <span style={styles.statusIcon}>{status.icon}</span>
            <span>{status.label}</span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close chat"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Closed banner */}
      {isClosed && (
        <div style={styles.closedBanner}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ marginRight: 6 }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          This conversation is closed. You can read previous messages but cannot send new ones.
        </div>
      )}

      {/* Messages area */}
      <div ref={messagesContainerRef} style={styles.messagesContainer}>
        {loadingMessages && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner} />
            <span>Loading messages...</span>
          </div>
        )}

        {!loadingMessages && messages.length === 0 && (
          <div style={styles.emptyState}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: "#e5e7eb", marginBottom: 8 }}
            >
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            <p style={{ color: "#9ca3af", margin: 0, textAlign: "center" }}>
              No messages yet
              {!isClosed && (
                <>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Start the conversation
                  </span>
                </>
              )}
            </p>
          </div>
        )}

        <div style={styles.messagesList}>
          {messages.map((m) => {
            const mine = m.senderId === user.uid;
            const time = formatTime(m.createdAt);

            return (
              <div
                key={m.id}
                style={{
                  ...styles.messageWrapper,
                  justifyContent: mine ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    ...styles.messageGroup,
                    alignItems: mine ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      ...styles.messageBubble,
                      ...(mine ? styles.myMessage : styles.theirMessage),
                    }}
                  >
                    {m.text}
                  </div>
                  {time && (
                    <span style={styles.messageTime}>{time}</span>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input area */}
      <form onSubmit={handleSend} style={styles.inputContainer}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            isClosed
              ? "Chat is closed"
              : "Type a message..."
          }
          disabled={isClosed || sending}
          style={{
            ...styles.input,
            ...(isClosed ? styles.inputDisabled : {}),
          }}
        />
        <button
          type="submit"
          disabled={!input.trim() || sending || isClosed}
          style={{
            ...styles.sendButton,
            ...(sending || isClosed || !input.trim()
              ? styles.sendButtonDisabled
              : {}),
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    maxWidth: 380,
    height: 550,
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },

  errorContainer: {
    width: 380,
    padding: 24,
    borderRadius: 16,
    background: "rgba(255, 255, 255, 0.95)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
  },

  errorMessage: {
    fontSize: 14,
    color: "#6b7280",
    textAlign: "center",
  },

  header: {
    padding: "12px 16px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(8px)",
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
  },

  headerInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  headerName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },

  headerSubtitle: {
    fontSize: 12,
    color: "#6b7280",
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    padding: "5px 10px",
    borderRadius: 12,
    fontWeight: 500,
  },

  statusIcon: {
    fontSize: 10,
  },

  closeButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    color: "#6b7280",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  closedBanner: {
    padding: "8px 16px",
    fontSize: 12,
    color: "#6b7280",
    background: "rgba(249, 250, 251, 0.8)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    background: "linear-gradient(to bottom, rgba(249, 250, 251, 0.5) 0%, rgba(255, 255, 255, 0.9) 100%)",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  loadingContainer: {
    padding: 32,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    fontSize: 13,
    color: "#9ca3af",
  },

  loadingSpinner: {
    width: 24,
    height: 24,
    border: "2px solid #f3f4f6",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
  },

  messagesList: {
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  messageWrapper: {
    display: "flex",
    marginBottom: 4,
  },

  messageGroup: {
    maxWidth: "75%",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  messageBubble: {
    padding: "8px 12px",
    borderRadius: 12,
    fontSize: 14,
    lineHeight: 1.5,
    wordWrap: "break-word",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },

  myMessage: {
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "#ffffff",
    boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    borderBottomRightRadius: 4,
  },

  theirMessage: {
    background: "rgba(0, 0, 0, 0.05)",
    color: "#111827",
    borderBottomLeftRadius: 4,
  },

  messageTime: {
    fontSize: 10,
    color: "#9ca3af",
    paddingInline: 4,
  },

  inputContainer: {
    padding: 12,
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
    display: "flex",
    gap: 8,
    background: "rgba(255, 255, 255, 0.9)",
  },

  input: {
    flex: 1,
    borderRadius: 20,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    padding: "10px 16px",
    fontSize: 14,
    outline: "none",
    background: "#ffffff",
    color: "#111827",
    transition: "border-color 0.2s ease",
  },

  inputDisabled: {
    background: "rgba(0, 0, 0, 0.02)",
    color: "#9ca3af",
    cursor: "not-allowed",
  },

  sendButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    border: "none",
    background: "#3b82f6",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0,
  },

  sendButtonDisabled: {
    background: "rgba(0, 0, 0, 0.05)",
    color: "#9ca3af",
    cursor: "not-allowed",
  },
};

// Add animation styles
if (typeof document !== "undefined" && !document.querySelector("#chat-styles")) {
  const style = document.createElement("style");
  style.id = "chat-styles";
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    /* Custom scrollbar for messages */
    div::-webkit-scrollbar {
      width: 6px;
    }
    
    div::-webkit-scrollbar-track {
      background: transparent;
    }
    
    div::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
    
    div::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.2);
    }
    
    /* Input focus style */
    input:focus {
      border-color: rgba(59, 130, 246, 0.4) !important;
    }
    
    /* Button hover effects */
    button:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    /* Close button hover */
    .close-button:hover {
      background: rgba(0, 0, 0, 0.05) !important;
    }
    
    /* Send button hover */
    .send-button:hover:not(:disabled) {
      background: #2563eb !important;
    }
  `;
  document.head.appendChild(style);
}

export default AppointmentChat;